import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

/**
 * Path-based tenant entry: `/t/<slug>/...`
 *
 * Stores the slug in localStorage (so TenantTheme/api can pick it up) and
 * forwards to the persona-appropriate route inside the React app.
 *
 *   /t/<slug>                    → /app/home              (customer landing)
 *   /t/<slug>/delivery/login     → /delivery/login        (rider login)
 *   /t/<slug>/admin/...          → /admin/...             (tenant admin)
 *   /t/<slug>/<anything-else>    → /<anything-else>       (passthrough)
 *
 * Native Capacitor apps embed the persona-specific URL as their webview
 * server.url (e.g. customer apps point at `/t/<slug>/`, rider apps point at
 * `/t/<slug>/delivery/login`), so this route MUST forward correctly to avoid
 * dumping rider users on the customer home.
 */
export default function TenantRedirect() {
  const { slug } = useParams();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!slug) return;
    try { localStorage.setItem("fd_tenant_slug", slug); } catch { /* private mode */ }

    // The wildcard segment after `/t/<slug>/` (may be empty).
    // useParams gives us `*` for a `/t/:slug/*` route.
    const prefix = `/t/${slug}`;
    let rest = loc.pathname.startsWith(prefix) ? loc.pathname.slice(prefix.length) : "";
    if (rest.startsWith("/")) rest = rest.slice(1);

    // Build the forward target. Keep query string + hash intact.
    const search = loc.search || "";
    const sep = search ? (search.includes("?") ? "&" : "?") : "?";
    const tenantQS = `${sep}t=${encodeURIComponent(slug)}`;
    const hash = loc.hash || "";

    let target;
    if (!rest) {
      // /t/<slug>  →  customer home
      target = `/app/home${search}${tenantQS}${hash}`;
    } else {
      // /t/<slug>/<anything>  →  /<anything> (preserves the deep link)
      target = `/${rest}${search}${tenantQS}${hash}`;
    }
    nav(target, { replace: true });
  }, [slug, loc.pathname, loc.search, loc.hash, nav]);

  return (
    <div className="min-h-screen pt-safe flex items-center justify-center text-neutral-500" data-testid="tenant-redirect">
      Loading {slug}…
    </div>
  );
}
